// DensityToolbar — the density-view header: the range span label
// (e.g. "Apr 2026 → Sep 2026"), a hint that the heatmap drives the
// agenda, and a Quarter | 6 months | 12 months segmented range toggle
// (default 6m; 12m is the strategic capacity-pinch-point view, spec
// §9.1). Controlled/presentational — the page owns the URL `range`
// state. The segmented control reuses the brand active treatment.
//
// `DensityRange` is owned by utils/schedule (single source of truth for
// the range vocabulary + the getDensitySpan helper); re-exported here
// so existing callers that import `DensityRange` from this module
// continue to compile.

export type { DensityRange } from "../../../utils/schedule";
import type { DensityRange } from "../../../utils/schedule";

interface DensityToolbarProps {
  rangeLabel: string;
  range: DensityRange;
  onRangeChange: (next: DensityRange) => void;
}

const RANGE_OPTIONS: { value: DensityRange; label: string }[] = [
  { value: "quarter", label: "Quarter" },
  { value: "6m", label: "6 months" },
  { value: "12m", label: "12 months" },
];

const SEG_BASE =
  "px-3 py-1 text-body-sm font-medium rounded transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";
const SEG_ACTIVE =
  "bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-slate-100";
const SEG_INACTIVE =
  "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200";

export default function DensityToolbar({
  rangeLabel,
  range,
  onRangeChange,
}: DensityToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-h3 text-slate-900 dark:text-slate-50">
          {rangeLabel}
        </h2>
        <p className="text-caption text-slate-500 dark:text-slate-400">
          Click any day on the heatmap to drive the agenda
        </p>
      </div>

      <div
        role="group"
        aria-label="Density range"
        className="inline-flex gap-0 rounded-md bg-slate-100 p-0.5 dark:bg-slate-700"
      >
        {RANGE_OPTIONS.map((opt) => {
          const active = range === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => onRangeChange(opt.value)}
              className={`${SEG_BASE} ${active ? SEG_ACTIVE : SEG_INACTIVE}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
